import {
  // createEventSettingDateItem,
  createEventSettingTicketItem,
  createEventSettingLimitItem,
  createEventSettingTriggerItem,
} from "./event.setting";
import { createEventDynamicModuleMap } from "./event.module";
import { createEventMediaGalleryList } from "./event.media";

/**
 * @typedef {import("./event.setting").EventDateSettingItem} EventDateSettingItem
 * @typedef {import("./event.setting").EventTicketSettingItem} EventTicketSettingItem
 * @typedef {import("./event.setting").EventLimitSettingItem} EventLimitSettingItem
 * @typedef {import("./event.setting").EventTriggerSettingItem} EventTriggerSettingItem
 * @typedef {import("./event.setting").EventImageFrameSettingItem} EventImageFrameSettingItem
 * @typedef {import("./event.module").EventDynamicModuleItem} EventDynamicModuleItem
 * @typedef {import("./event.module").EventDynamicModuleMap} EventDynamicModuleMap
 */

/**
 * Event Structure Item Object Definition
 * @typedef {Object} EventStructureItem
 * @property {Object} data - The main data object for the event
 * @property {string|null} data.EventId - The unique identifier for the event
 * @property {string|null} data.UserId - The unique identifier for the user owner who created the event
 * @property {string|null} data.Url - The URL slug for the event (e.g. "my_event")
 * @property {string|null} data.Country - The country 2-letter code (lowercase) where the event is taking place (e.g. "us")
 * @property {string|null} data.Currency - The currency 3-letter code (lowercase) used for the event (e.g. "usd")
 * @property {boolean} data.Active - Whether the event is active
 * @property {boolean} data.LaunchPerTicket - Whether to launch the event per ticket type or across all types
 * @property {string} data.Access - The access level for the event (e.g. "public", "private")
 * @property {string|null} data.Privacy - Additional privacy settings placeholder for the event
 * @property {string|null} data.Tagline - A short tagline for the event
 * @property {string|null} data.Details - Detailed information about the event
 * @property {string|null} data.WelcomeDetails - Welcome message details for the event
 * @property {string|null} data.Logo - The image location id for the event logo
 * @property {EventTicketSettingItem[]} data.TicketTypes - An array of ticket type settings for the event
 * @property {EventTriggerSettingItem[]} data.Triggers - An array of trigger settings for the event
 * @property {EventLimitSettingItem[]} data.Limits - An array of limit settings for the event
 * @property {EventImageFrameSettingItem[]} data.ImageFrames - An array of image frame settings for the event
 * @property {Array} data.Ticking - An array of ticking items for the event (e.g. attendees)
 * @property {Array} data.Dates - An array of date items for the event (e.g. start and end dates)
 * @property {Array} data.Badges - An array of badge items for the event (e.g. VIP badges)
 * @property {Array} data.Socials - An array of social media items for the event (e.g. Facebook, Twitter)
 * @property {boolean} hydrated - Whether the event data has been fully loaded and hydrated
 * @property {boolean} exploring - Whether the event is currently being explored (viewed by a guest user not logged in)
 * @property {boolean} owner - Whether the current user is the owner of the event
 * @property {boolean} manager - Whether the current user is a manager of the event
 * @property {boolean} pinned - Whether the event is pinned by the user to their inbox for quick access
 * @property {boolean} accessQueued - Whether access to the event is currently queued for approval
 * @property {boolean} accessGranted - Whether access to the event has been granted
 * @property {Object} urlComponents - An object containing components of the event URL
 * @property {string|null} urlComponents.pointer - The country pointer component of the URL (e.g. "/us/")
 * @property {string|null} urlComponents.name - The name component of the URL (e.g. "my_event")
 * @property {string|null} urlComponents.absolute - The absolute URL for the event (e.g. "https://entzy.com/us/my_event")
 * @property {string|null} urlComponents.relative - The relative URL for the event (e.g. "/us/my_event")
 * @property {string} moduleSelected - The currently selected module for the event (e.g. "launchpad", "dashboard")
 * @property {EventDynamicModuleItem} moduleHasSelected - An object tracking which modules have been selected by the user
 * @property {EventDynamicModuleMap} modules - An object containing module settings for the event
 * @property {Object} lastDataUpdate - An object tracking the last data update for the event
 * @property {string|null} lastDataUpdate.key - The key of the last updated data field
 * @property {number|null} lastDataUpdate.time - The timestamp of the last data update
 * @property {Object} generators - An object containing generator functions for creating event settings
 * @property {function} generators.settings.date - A function to generate date setting items for the event
 * @property {function} generators.settings.ticket - A function to generate ticket setting items for the event
 * @property {function} generators.settings.limit - A function to generate limit setting items for the event
 * @property {function} generators.settings.trigger - A function to generate trigger setting items for the event
 */

/**
 * Create Event Structure Item Object
 * @return {EventStructureItem} The created Event Structure Item Object
 */

export function createEventStructureItem() {
  return {
    data: {
      EventId: null, // override with actual event id when creating or loading event
      UserId: null,
      Url: null,
      Country: null,
      Currency: null,
      Active: true,
      Access: "public",
      Privacy: null,
      Tagline: null,
      Details: null,
      WelcomeDetails: null,
      LaunchPerTicket: false,
      Logo: null,
      TicketTypes: [
        createEventSettingTicketItem({
          type: "default",
        }),
      ],
      Triggers: [
        createEventSettingTriggerItem({
          type: "default",
          order: 1,
        }),
        createEventSettingTriggerItem({
          type: "financial",
          order: 2,
        }),
      ],
      Limits: [
        createEventSettingLimitItem({
          type: "notice",
          order: 1,
        }),
        createEventSettingLimitItem({
          type: "capacity",
          order: 2,
        }),
      ],
      ImageFrames: createEventMediaGalleryList(),
      Ticking: [],
      Dates: [],
      Badges: [],
      Socials: [],
    },
    hydrated: false,
    exploring: false,
    owner: false,
    manager: false,
    pinned: null, // initial null state required before loaded
    accessQueued: false,
    accessGranted: false,
    urlComponents: {
      pointer: null,
      name: null,
      absolute: null,
      relative: null,
    },
    moduleSelected: "launchpad",
    moduleHasSelected: {},
    modules: createEventDynamicModuleMap(),
    lastDataUpdate: {
      key: null,
      time: null,
    },
  };
}

export function createEventStructureConstants() {
  const constants = {};

  constants.maxNameLength = 30;
  constants.maxTaglineLength = 50;
  constants.maxDetailsLength = 20000;

  constants.minLaunchNotice = 0;
  constants.midLaunchNotice = 7;
  constants.maxLaunchNotice = 180;

  constants.minLaunchCapacity = 2;
  constants.midLaunchCapacity = 100;
  constants.maxLaunchCapacity = 1000;

  // legacy variable naming
  constants.minLaunchGuests = 1; // phase out
  constants.midLaunchGuests = 50; // phase out
  constants.maxLaunchGuests = 1000; // phase out
  // use below for alignment with trigger setting
  constants.minLaunchQuantity = 1;
  constants.midLaunchQuantity = 50;
  constants.maxLaunchQuantity = 1000;

  // note: placeholder for financial triggers on amounts
  constants.minLaunchFinancial = 100; // e.g. $1.00
  constants.midLaunchFinancial = 50000; // e.g. $500.00
  constants.maxLaunchFinancial = 1000000; // e.g. $10,000.00

  constants.cacheExpiry = 2;
  constants.states = [
    { value: "owner", display: "owner", runner: true },
    { value: "manager", display: "manager", runner: true },
    { value: "ticked", display: "ticked", runner: false },
    { value: "pinned", display: "pinned", runner: false },
    { value: "owner-restored", display: "owner", runner: true },
    { value: "manager-restored", display: "manager", runner: true },
    { value: "restored", display: "restored", runner: false },
  ];
  constants.pinnedRunnerStates = [];
  constants.pinnedRiderStates = [];
  for (let state of constants.states) {
    if (state.runner) {
      constants.pinnedRunnerStates.push(state.value);
    } else {
      constants.pinnedRiderStates.push(state.value);
    }
  }

  constants.meetCategories = [
    {
      id: "cuppa",
      title: "Let's have a regular brew",
      tagline: "Get coffee, spill the tea, catch up",
      details:
        "Get together for a regular coffee fix, or to spill the tea over a hot brew.",
      activity: true,
    },
    {
      id: "diy",
      title: "Let's build stuff",
      tagline: "Community projects, DIY, share skills",
      details:
        "Bring a team together when needed to build things in the community.",
      activity: true,
    },
    {
      id: "food",
      title: "Let's have a regular feast",
      tagline: "Dine out, eat in, share meals together",
      details:
        "Head to a restaurant, host dinner parties, get takeouts, or regularly share a feast of a meal together.",
      activity: true,
    },
    {
      id: "drinks",
      title: "Let's get some drinks in",
      tagline: "Drinks somewhere in a pub, bar, garden",
      details:
        "Catch up for regular drinks somewhere in a local pub, a bar, beer garden, or at home.",
      activity: true,
    },
    {
      id: "games",
      title: "Let's do some game nights",
      tagline: "Board games, computer games, card games",
      details:
        "Let's find time to regularly roll the dice, play some cards, or game online.",
      activity: true,
    },
    {
      id: "hibernate",
      title: "Let's just relax indoors",
      tagline: "Relax somewhere inside, do nothing",
      details:
        "An intentional plan to do nothing regularly, hang out indoors, read, relax together.",
      activity: true,
    },
    {
      id: "lunch",
      title: "Let's lunch regularly",
      tagline: "Grab lunch, brunch, daytime snacks",
      details:
        "Find some regular slots in the day for lunch, brunch, all day breakfast, or light bites.",
      activity: true,
    },
    {
      id: "stream",
      title: "Let's have regular screen time",
      tagline: "Catch movies, stream, binge-watch",
      details:
        "Carve out time together to regularly stream the latest movies, shows, or series.",
      activity: true,
    },
    {
      id: "sunseek",
      title: "Let's absorb plenty of vitamin D",
      tagline: "Relax outdoors, parks, beaches",
      details:
        "Enjoy regular outdoor activities together in parks, at the beach, or anything in sun.",
      activity: true,
    },
    {
      id: "shop",
      title: "Let's do regular retail therapy",
      tagline: "Hit the shops, malls, markets",
      details:
        "Get together for regular retail sessions, mall crawls, shopathons.",
      activity: true,
    },
    {
      id: "sport",
      title: "Let's get active regularly",
      tagline: "Play sports, watch sports, train together",
      details:
        "Get active with regular sports sessions, matches, or watch games together.",
      activity: true,
    },
    {
      id: "volunteer",
      title: "Let's do some good regularly",
      tagline: "Community outreach, volunteer work, support",
      details:
        "Offer or join regular support and outreach to people in need in the community.",
      activity: true,
    },
    {
      id: "walk",
      title: "Let's get walking regularly",
      tagline: "Get the steps in, take walks, hikes",
      details:
        "Get moving with regular walks, hikes, or casual strolls in the area.",
      activity: true,
    },
    {
      id: "nights",
      title: "Let's have a regular late night",
      tagline: "Club nights, music sessions, all nighters",
      details:
        "Regular late night sessions at a club or anywhere with the right music.",
      activity: true,
    },
    // place based categories
    {
      id: "home",
      title: "Let's meet at my place",
      tagline: "I can host for regular gatherings",
      details:
        "Available to host regularly at my place for meals, drinks, games, movies, or just to hang out.",
      place: true,
    },
    {
      id: "cafe",
      title: "Let's meet at this cafe",
      tagline: "I've got a great local cafe for regular meets",
      details:
        "I have a lovely local cafe that's perfect for regular catch ups over coffee, tea, snacks, or light meals.",
      place: true,
    },
    {
      id: "studio",
      title: "Let's meet at my studio",
      tagline: "I've got a creative space, workshop, arts hub",
      details:
        "I have a creative studio space that's ideal for regular meets focused on creativity and collaboration.",
      place: true,
    },
    {
      id: "pub",
      title: "Let's meet at this pub",
      tagline: "I've found a great local pub for regular drinks",
      details: "I've discovered a pub that's perfect for catch up drinks.",
      place: true,
    },
    {
      id: "workshop",
      title: "Let's meet at my workshop",
      tagline: "I've got a workshop space for regular builds",
      details:
        "I have a workshop space that's great for regular DIY projects and collaborative builds.",
      place: true,
    },
    {
      id: "gym",
      title: "Let's meet at the gym",
      tagline: "I can host regular fitness sessions",
      details:
        "I have access to a gym where we can have regular fitness sessions together.",
      place: true,
    },
    {
      id: "workspace",
      title: "Let's meet at my workspace",
      tagline: "I've got a co-working space for regular meetings",
      details:
        "I have a co-working space that's perfect for regular work-focused meetings.",
      place: true,
    },
    {
      id: "track",
      title: "Let's meet at the track",
      tagline: "I know a great track for regular runs, cycles",
      details:
        "I have a favorite track ideal for regular running or cycling sessions.",
      place: true,
    },
    {
      id: "stadium",
      title: "Let's meet at the stadium",
      tagline: "I can get us regular spots for sports, concerts",
      details:
        "I can secure regular spots at the local stadium for sports events or concerts.",
      place: true,
    },
    {
      id: "park",
      title: "Let's meet at the park",
      tagline: "I know a great park for regular outdoor meets",
      details:
        "I have a favorite park that's perfect for regular outdoor gatherings and activities.",
      place: true,
    },
    {
      id: "beach",
      title: "Let's meet at the beach",
      tagline: "I know a great beach for regular sun, surf, sand",
      details:
        "I have a favorite beach ideal for regular sunbathing, surfing, or sandcastle building.",
      place: true,
    },
    {
      id: "garden",
      title: "Let's meet at my garden",
      tagline: "I can host regular outdoor gatherings",
      details:
        "I have a lovely garden that's perfect for hosting regular outdoor gatherings and barbecues.",
      place: true,
    },
    {
      id: "rooftop",
      title: "Let's meet at my rooftop",
      tagline: "I can host regular skyline gatherings",
      details:
        "I have a rooftop space with great views, ideal for regular social gatherings.",
      place: true,
    },
    {
      id: "gallery",
      title: "Let's meet at the gallery",
      tagline: "I know a great gallery for regular art meets",
      details:
        "I have a favorite art gallery that's perfect for regular cultural and artistic gatherings.",
      place: true,
    },
  ];

  return constants;
}
